/**
 * @file purchase-order-list.tsx
 * @deprecated Copied to app/modules/procurement/pages/po-management/purchase-order-list.tsx. This file still serves the existing /UProcure routes.
 * @module app/modules/uProcure/po-management/purchase-order-list
 * @description Display paginated list of purchase orders with advanced filtering, search,
 *   date range selection, and vendor/category filters. Integrates warehouse context for
 *   multi-warehouse filtering and uses react-table for responsive column management.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 *
 * @todo [P1] Route-level permission guard: enforce PO list access in route wrapper/PrivateRoutes
 *   to prevent direct URL access when user lacks module permission.
 * @todo [P1] Edit functionality parity: add row-level edit action with status/permission guards
 *   and navigation to /UProcure/purchase-order-management/edit-purchase-order.
 * @todo [P1] Delete functionality parity: implement row-level delete flow (if endpoint is
 *   available in utrade backend) with confirmation modal and post-delete list refresh.
 * @todo [P2] Audit log parity: align list-screen destructive/critical actions with backend-friendly
 *   audit payloads (not only client-side logs) for migration parity.
 * @todo [P2] Status/filter parity: verify status values and filter mapping match backend contract
 *   for cancelled/rejected/partial-delivered states.
 * @todo [P1] API error-state hardening: wrap list/category/vendor fetches in try/finally so
 *   loading flags are always reset and users see actionable error toasts when requests fail.
 * @todo [P1] Request race protection: cancel or ignore stale fetchPurchaseOrders responses when
 *   filters/search/tab/page change quickly to prevent older responses from overwriting newer state.
 * @todo [P2] Pagination boundary guard: prevent next/previous handlers from requesting out-of-range
 *   pages when users are already at first/last page.
 * @todo [P2] Persisted filter consistency: standardize saved value shape/types (Date/string/json)
 *   for listPurchaseOrder keys to avoid malformed-state cleanup workarounds.
 * @todo [P3] Dead callback cleanup: remove or implement no-op select callbacks
 *   (onMenuScrollToBottom/onMenuClose/handleInputChange) to reduce maintenance noise.
 */

import { ColumnDef } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import { useLocation, useNavigate } from "react-router-dom";
import { getKey, setKey, removeKey } from "../../../../Global/history";
import {
  Admin,
  AllVendors,
  CartonWithDozens,
  CartonWithPieces,
  Dozen,
  PAGE_LIMIT,
  PermissionModule,
  Permissions,
  Piece,
  ShipmentCancelled,
  ShipmentDelivered,
  ShipmentPartialDelivered,
  ShipmentRejected,
} from "../../../../utils/constants";
import Method from "../../../../utils/methods";
import { listPurchaseOrder } from "../../../../utils/storeString";
import { useDebounce } from "../../../../utils/useDebounce";
import { CustomTable } from "../../../custom/data-table";
import CustomDatePicker from "../../../custom/DateRange/CustomDatePicker";
import DynamicListLayout from "../../../custom/layouts/DynamicListLayout";
import { CustomSearchInput } from "../../../custom/search";
import GoodInfoRejected from "../../../modals/good-request-info-reject";
import ViewGoodsRequestProducts from "../../../modals/view-requested-goods-products";
import { useAuth } from "../../auth";
import { PURCHASEORDER, vendor } from "../../../../api/apiEndPoints";
import APICallService from "../../../../api/apiCallService";
import { CustomSelect } from "../../../custom/Select/CustomSelect";
import { useWarehouse } from "../../../context/WarehouseContext";
import { ElementAnimateUtil } from "../../../../_admin/assets/ts/_utils";
import { master } from "../../../../api/apiEndPoints";
import b1 from "../../../../_admin/assets/media/product/default.svg";
import editIcon from "../../../../_admin/assets/media/svg_uTrade/edit-round-blue.svg";
import CrossIcon from "../../../../_admin/assets/media/svg_uTrade/cross-rounded-blue.svg";
import greenCheck from "../../../../_admin/assets/media/svg_uTrade/green_check.svg";
import { error, success } from "../../../../Global/toast";
import ImportPOModal from "../../../modals/import-po-excel";
import DraftPoList, { DRAFT_PO_TAB, fetchDraftPos } from "./draft-po-list";

type PurchaseOrder = {
  shipment: {
    type: number;
    date: string;
    phone?: string;
    address?: string;
  };
  requestor: {
    user: string;
    userType: number;
  };
  warehouse: {
    reference: string;
    name: string;
    phoneCountry: string;
    phone: string;
    address: string;
    lat: number;
    lng: number;
    defaultWarehouse: boolean;
    district: any[];
    undefined: any[];
  };
  business: {
    reference:
      | string
      | {
        _id?: string;
        workingDays?: number[];
      };
    owner: string;
    name: string;
    ownerName: string;
    image: string;
  };
  _id: string;
  type: string;
  refKey: string;
  variants: Array<{
    product: string;
    variant: {
      _id: string;
      refKey: string;
      title: string;
      variantType: [
        {
          reference: string;
          option: string;
        }
      ];
      media: [
        {
          url: string;
          type: number;
          index: number;
          _id: string;
          thumbnailUrl: string;
        }
      ];
      type: string;
    };
    clonedRef: string;
    quantityTypes: Array<{
      dimensions: {
        length: number;
        width: number;
        height: number;
        weight: number;
      };
      type: number;
      receivedStockCount: number;
      rejectedStockCount: number;
      amount: number;
      discountAmt: number;
      discountAmtEnabled: boolean;
      discountByQuantitiesEnabled: boolean;
      discountsByQuantities: [
        {
          min: number;
          max: number;
          discountAmt: number;
          _id: string;
        }
      ];
      sellingAmount: number;
      sellingDiscountAmt: number;
      sellingDiscountAmtEnabled: boolean;
      sellingDiscountByQuantitiesEnabled: boolean;
      purchaseAmount: number;
      purchaseDiscountAmt: number;
      sellingDiscountsByQuantities: [];
      purchaseDiscountsByQuantities: [];
      stockCount: number;
    }>;
  }>;
  businesses: [
    {
      reference: string;
    }
  ];
  totalQuantities: [
    {
      quantityType: number;
      stockCount: number;
    }
  ];
  totalWeight: number;
  status: number;
  statusUpdatedAt: string;
  charge: number;
  currency: number;
  otp: string;
  versionKey: string;
  statusesLogs: [
    {
      _id: string;
      status: number;
      statusUpdatedAt: string;
    }
  ];
  deliveries: any[];
  _createdAt: string;
  _updatedAt: string;
};

const getCategoryLabel = (val: any) => (
  <div className="d-flex align-items-center">
    <div className="symbol symbol-30px border me-2">
      <img src={val.image || b1} className="object-fit-contain" alt="" />
    </div>
    <span className="fs-15 fw-600">{val.name || val.title}</span>
  </div>
);

const getSubCategoryLabel = (val: any) => (
  <div className="d-flex align-items-center">
    <span className="fs-15 fw-600">{val.name || val.title}</span>
  </div>
);

const getStoredJson = (key: string) => {
  const val = getKey(key);
  if (!val || typeof val !== "string") return null;
  try {
    return JSON.parse(val);
  } catch {
    return null;
  }
};

const sanitizeForStorage = (val: any): any => {
  if (!val || typeof val !== 'object') return val;
  if (Array.isArray(val)) return val.map(sanitizeForStorage);

  const { label, ...rest } = val;
  const sanitized: any = { ...rest };
  Object.keys(sanitized).forEach(key => {
    if (sanitized[key] && typeof sanitized[key] === 'object') {
      sanitized[key] = sanitizeForStorage(sanitized[key]);
    }
  });
  return sanitized;
};

const PurchaseOrderList = () => {
  const navigate = useNavigate();
  const { currentUser }: any = useAuth();
  const [goods, setGoods] = useState<any>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showRequestedGoods, setRequestedGoods] = useState<boolean>(false);
  const [selectedOrderProducts, setSelectedOrderProducts] = useState<any>([]);
  const [showImportModal, setShowImportModal] = useState<boolean>(false);
  const { state }: any = useLocation();
  const { selectedWarehouse } = useWarehouse();

  const [page, setPage] = useState<any>(getKey(listPurchaseOrder.page) || 1);
  const [pageLimit, setPageLimit] = useState<any>(
    getKey(listPurchaseOrder.limit) || PAGE_LIMIT
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState<string | any>(() => {
    const savedSearch = getKey(listPurchaseOrder.search);
    return savedSearch ? savedSearch : "";
  });
  const [startDate, setStartDate] = useState<any>(() => {
    const savedDate = getKey(listPurchaseOrder.filterStartDate);
    return savedDate ? new Date(savedDate.toString()) : null;
  });
  const [endDate, setEndDate] = useState<any>(() => {
    const savedDate = getKey(listPurchaseOrder.filterEndDate);
    return savedDate ? new Date(savedDate.toString()) : null;
  });

  const storedCategoryRaw = getStoredJson(listPurchaseOrder.category);
  const storedSubCategoryRaw = getStoredJson(listPurchaseOrder.subCategory);
  const storedGroupCategoryRaw = getStoredJson(listPurchaseOrder.groupCategory);

  const storedCategory = storedCategoryRaw ? {
    ...storedCategoryRaw,
    label: getCategoryLabel(storedCategoryRaw),
    subCategories: (storedCategoryRaw.subCategories || []).map((sub: any) => ({
      ...sub,
      label: getSubCategoryLabel(sub),
      groupCategories: (sub.groupCategories || []).map((group: any) => ({
        ...group,
        label: getSubCategoryLabel(group),
      })),
    }))
  } : null;

  const storedSubCategory = storedSubCategoryRaw ? {
    ...storedSubCategoryRaw,
    label: getSubCategoryLabel(storedSubCategoryRaw),
    groupCategories: (storedSubCategoryRaw.groupCategories || []).map((group: any) => ({
      ...group,
      label: getSubCategoryLabel(group),
    }))
  } : null;

  const storedGroupCategory = storedGroupCategoryRaw ? {
    ...storedGroupCategoryRaw,
    label: getSubCategoryLabel(storedGroupCategoryRaw)
  } : null;

  const [selectedCategory, setSelectedCategory] = useState<any>(storedCategory);
  const [selectedSubCategory, setSelectedSubCategory] = useState<any>(storedSubCategory);
  const [subCategories, setSubCategories] = useState<any>(storedCategory?.subCategories || []);
  const [selectedGroupCategory, setSelectedGroupCategory] = useState<any>(storedGroupCategory);
  const [groupCategories, setGroupCategories] = useState<any>(storedSubCategory?.groupCategories || []);
  const [categories, setCategories] = useState<any>([]);
  const [fetchCategoryLoader, setFetchCategoryLoader] = useState(false);
  const [categoryPage, setCategoryPage] = useState(1);
  const [totalCategory, setTotalCategory] = useState(0);
  const [categorySearch, setCategorySearch] = useState("");
  const [tabKey, setTabKey] = useState<any>(() => {
    const savedTabRaw = getKey(listPurchaseOrder.currentTab);
    const savedTab = savedTabRaw != null && savedTabRaw !== ""
      ? parseInt(String(savedTabRaw), 10)
      : null;
    const navTabRaw = state?.tab ?? state;
    const navTab =
      navTabRaw != null && navTabRaw !== ""
        ? parseInt(String(navTabRaw), 10)
        : null;

    const isAdminOrSuper =
      currentUser?.userType === Admin ||
      currentUser?.roleAndPermission?.roleType === "R0001" ||
      currentUser?.roleAndPermission?.roleType === "R0003" ||
      currentUser?.roleAndPermission?.roleType === "R0012";

    const tabPermMap: Record<number, number> = {
      1: Permissions.Pending_Tab,
      2: Permissions.Approved_Tab,
      5: Permissions.In_Process_Tab,
      9: Permissions.Completed_Tab,
      4: Permissions.Rejected_Tab,
      3: Permissions.Cancelled_Tab,
      [DRAFT_PO_TAB]: Permissions.Draft_PO_Tab,
    };

    const hasPermForTab = (tabValue: number): boolean =>
      isAdminOrSuper ||
      Method.hasPermission(PermissionModule.POManagement, tabPermMap[tabValue], currentUser);

    // All tabs in display order
    // Draft PO is the first tab
    const allTabValues = [DRAFT_PO_TAB, 1, 2, 5, 9, 4, 3];

    // Tabs the user has permission to see
    const permittedTabValues = allTabValues.filter(hasPermForTab);

    // Prefer navigation state (e.g. after creating a PO) over sticky saved tab
    if (navTab !== null && !Number.isNaN(navTab) && permittedTabValues.includes(navTab)) {
      return navTab;
    }

    if (savedTab !== null && !Number.isNaN(savedTab) && permittedTabValues.includes(savedTab)) {
      return savedTab;
    }

    // 3. Warehouse Manager Role -> Default to Approved (2) if permitted
    if (currentUser?.roleAndPermission?.isWarehouseManagerRole && hasPermForTab(2)) {
      return 2;
    }

    // 4. Default to first permitted tab
    return permittedTabValues[0] ?? allTabValues[0];
  });
  const [dataFetched, setDataFetched] = useState(false);
  const [draftCount, setDraftCount] = useState<number>(NaN);
  const [draftTotal, setDraftTotal] = useState<number>(0);

  // Settings "Old PO after (days)" of the warehouse, for the Days Open flag (read once per warehouse)
  const [oldPoDays, setOldPoDays] = useState<number | null>(null);
  useEffect(() => {
    if (!selectedWarehouse?.value) return;
    const api = new APICallService(PURCHASEORDER.poSettings, { warehouse: selectedWarehouse.value }, "", "", "", "", PermissionModule.POManagement, selectedWarehouse.value);
    api.callAPI().then((res: { oldPoDays?: number } | null) => setOldPoDays(typeof res?.oldPoDays === "number" ? res.oldPoDays : null));
  }, [selectedWarehouse?.value]);

  // Draft PO tab count for the selected warehouse
  useEffect(() => {
    if (!selectedWarehouse?.value || !hasTabPermission(DRAFT_PO_TAB)) return;
    fetchDraftPos(selectedWarehouse.value).then((res) => setDraftCount(res?.total ?? 0));
  }, [selectedWarehouse?.value]);
  const [editingDeliveryDateId, setEditingDeliveryDateId] = useState<string | null>(null);
  const [editingDeliveryDateValue, setEditingDeliveryDateValue] = useState<string>("");
  const [savingDeliveryDateId, setSavingDeliveryDateId] = useState<string | null>(null);

  const navigateToOrderDetails = (order: PurchaseOrder) => {
    const currentScrollY = window.scrollY;
    setKey(listPurchaseOrder.scrollPosition, currentScrollY);
    setKey(listPurchaseOrder.search, search, false);

    navigate(
      "/UProcure/purchase-order-management/purchase-order-details",
      {
        state: { id: order._id, status: order.status },
      }
    );
  };

  const navigateToGrnSection = (order: PurchaseOrder) => {
    const currentScrollY = window.scrollY;
    setKey(listPurchaseOrder.scrollPosition, currentScrollY);
    setKey(listPurchaseOrder.search, search, false);

    navigate(
      "/UProcure/purchase-order-management/purchase-order-details",
      {
        state: {
          id: order._id,
          status: order.status,
          openGrnSection: true,
        },
      }
    );
  };
  const onMenuScrollToBottom = async () => { };
  const onMenuClose = async () => { };
  const onMenuOpen = async () => {
    setPage(1);
  };
  const handleInputChange = async () => {
    setPage(1);
  };
    const handleVendor = async (event: any) => {
    const value = event?.value || "";
    setBusinessId(value);
    setPage(1);
    setKey(listPurchaseOrder.page, 1);
    if (value) {
      setKey(listPurchaseOrder.businessId, value, false);
    } else {
      removeKey(listPurchaseOrder.businessId);
    }
    await fetchPurchaseOrders(
      1,
      pageLimit,
      tabKey,
      search,
      startDate?.toISOString(),
      endDate?.toISOString(),
      undefined,
      undefined,
      undefined,
      value
    );
  }

  const toInputDate = (dateValue?: string) => {
    if (!dateValue) return "";
    const dt = new Date(dateValue);
    if (Number.isNaN(dt.getTime())) return "";
    return Method.convertDateToFormat(dt, "YYYY-MM-DD");
  };

  /** whole days since the PO was created (null when the date is missing) */
  const daysOpenOf = (createdAt: string): number | null => {
    const created = new Date(createdAt);
    if (Number.isNaN(created.getTime())) return null;
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startOfCreated = new Date(created.getFullYear(), created.getMonth(), created.getDate());
    return Math.max(0, Math.floor((startOfToday.getTime() - startOfCreated.getTime()) / (1000 * 60 * 60 * 24)));
  };

  const getDaysOpenLabel = (createdAt: string) => {
    const createdDate = new Date(createdAt);
    if (Number.isNaN(createdDate.getTime())) return "-";

    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startOfCreatedDate = new Date(
      createdDate.getFullYear(),
      createdDate.getMonth(),
      createdDate.getDate()
    );
    const dayDiff = Math.max(
      0,
      Math.floor(
        (startOfToday.getTime() - startOfCreatedDate.getTime()) /
        (1000 * 60 * 60 * 24)
      )
    );
    return `${dayDiff} day${dayDiff === 1 ? "" : "s"}`;
  };

  const canEditDeliveryDate = (status: number) => {
    const lockedStatuses = [
      ShipmentPartialDelivered,
      ShipmentDelivered,
      ShipmentCancelled,
      ShipmentRejected,
    ];
    return !lockedStatuses.includes(status) && Method.hasPermission(PermissionModule.POManagement,Permissions.PO_DELIVERY_DATE_UPDATE,currentUser);
  };

  const handleStartDeliveryDateEdit = (order: PurchaseOrder) => {
    if (!canEditDeliveryDate(order.status)) return;
    setEditingDeliveryDateId(order._id);
    setEditingDeliveryDateValue(toInputDate(order.shipment?.date));
  };

  const handleCancelDeliveryDateEdit = () => {
    setEditingDeliveryDateId(null);
    setEditingDeliveryDateValue("");
  };

  const handleSaveDeliveryDate = async (order: PurchaseOrder) => {
    if (!editingDeliveryDateValue) {
      error("Please select a delivery date");
      return;
    }

    setSavingDeliveryDateId(order._id);
    try {
      const apiService = new APICallService(
        PURCHASEORDER.updateDeliveryDate,
        { date: editingDeliveryDateValue },
        { id: order._id },
        "",
        "",
        "",
        "",
        selectedWarehouse?._id
      );
      const response = await apiService.callAPI();

      if (response) {
        success("Delivery date updated successfully");
        setGoods((prev: PurchaseOrder[]) =>
          prev.map((item) =>
            item._id === order._id
              ? {
                ...item,
                shipment: {
                  ...item.shipment,
                  date: editingDeliveryDateValue,
                },
              }
              : item
          )
        );
        handleCancelDeliveryDateEdit();
      } else {
        error("Failed to update delivery date");
      }
    } finally {
      setSavingDeliveryDateId(null);
    }
  };

  const isDeliveryDateSelectable = (date: Date, order: PurchaseOrder) => {
    const businessData: any = order?.business;
    const businessRef: any = businessData?.reference;

    const rawWorkingDays =
      (businessRef && typeof businessRef === "object" && Array.isArray(businessRef.workingDays)
        ? businessRef.workingDays
        : null) ||
      (Array.isArray(businessData?.workingDays) ? businessData.workingDays : []);

    const normalizedWorkingDays = rawWorkingDays
      .map((day: any) => (typeof day === "object" ? day?.day : day))
      .map(Number)
      .filter((day: number) => !Number.isNaN(day));

    if (!normalizedWorkingDays.length) return true;

    const usesOneBasedDays = normalizedWorkingDays.every(
      (day: number) => day >= 1 && day <= 7
    );
    const comparableWorkingDays = usesOneBasedDays
      ? normalizedWorkingDays.map((day: number) => day % 7)
      : normalizedWorkingDays;

    return comparableWorkingDays.includes(date.getDay());
  };

  const isAdminUser =
    currentUser?.userType === Admin ||
    currentUser?.roleAndPermission?.roleType === "R0001" ||
    currentUser?.roleAndPermission?.roleType === "R0003" ||
    currentUser?.roleAndPermission?.roleType === "R0012";

  const tabPermissionMap: Record<number, number> = {
    1: Permissions.Pending_Tab,
    2: Permissions.Approved_Tab,
    5: Permissions.In_Process_Tab,
    9: Permissions.Completed_Tab,
    4: Permissions.Rejected_Tab,
    3: Permissions.Cancelled_Tab,
    [DRAFT_PO_TAB]: Permissions.Draft_PO_Tab,
  };
  const [businessId, setBusinessId] = useState<string>(() => {
    const savedBusinessId = getKey(listPurchaseOrder.businessId);
    return typeof savedBusinessId === "string" ? savedBusinessId : "";
  });
  const [vendorData, setVendorData] = useState<any[]>([]);

  const hasTabPermission = (tabValue: number): boolean =>
    isAdminUser ||
    (tabPermissionMap[tabValue] !== undefined &&
      Method.hasPermission(
        PermissionModule.POManagement,
        tabPermissionMap[tabValue],
        currentUser
      ));

  const columns: ColumnDef<PurchaseOrder>[] = [
    {
      header: "PO Date & No",
      accessorKey: "refKey",
      cell: ({ row }) => {
        const order = row.original as PurchaseOrder;
        return (
          <div className="d-flex align-items-start flex-column">
            <span className="text-dark d-block">{order._createdAt ? Method.convertDateToDDMMYYYYHOURS(order._createdAt) : '-'}</span>
            <span className="text-gray d-block">{order.refKey}</span>
          </div>
        );
      },
    },
    {
      header: "Supplier Name",
      accessorKey: "vendorName",
      cell: ({ row }) => {
        const order = row.original as PurchaseOrder;
        return <span className="fs-15 fw-600">{order.business.ownerName}</span>;
      },
    },
    {
      header: "Products & Quantities",
      accessorKey: "products",
      cell: ({ row }) => {
        const order = row.original as PurchaseOrder;
        if (!order.variants || order.variants.length === 0) {
          return <span>-</span>;
        }

        // Aggregate receivedStockCount by quantity type across all variants
        const typeTotals: Record<number, number> = {};
        order.variants?.forEach((v) =>
          v.quantityTypes?.forEach((qt) => {
            const t = Number(qt.type);
            const cnt = Number(qt.stockCount) || 0;
            typeTotals[t] = (typeTotals[t] || 0) + cnt;
          })
        );

        const quantities = Object.keys(typeTotals)
          .map((k) => {
            const t = Number(k);
            return Method.formatQuantityDisplay(t, typeTotals[t]);
          })
          .filter(Boolean);
        return (
          <div className="d-flex flex-column align-items-start gap-3">
            <span className="fs-15 fw-600">{quantities.join(", ") || "-"}</span>
            <Button
              variant="link"
              className="btn-flush fs-16 fw-600 text-decoration-underline p-0"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedOrderProducts(order.variants);
                setRequestedGoods(true);
              }}
            >
              View products
            </Button>
          </div>
        );
      },
    },
    {
      header: "Delivery Location",
      accessorKey: "deliveryLocation",
      cell: ({ row }) => {
        const order = row.original as PurchaseOrder;
        return (
          <span className="fs-15 fw-500">
            {order.warehouse ? order.warehouse.address : "N/A"}
          </span>
        );
      },
    },
    {
      header: "Delivery Date",
      accessorKey: "deliveryDate",
      cell: ({ row }) => {
        const order = row.original as PurchaseOrder;
        const isEditing = editingDeliveryDateId === order._id;
        const canEdit = canEditDeliveryDate(order.status);
        const isSaving = savingDeliveryDateId === order._id;

        return (
          <div
            className="d-flex align-items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {isEditing ? (
              <div style={{ minWidth: 200 }}>
              <CustomDatePicker
                selected={editingDeliveryDateValue ? new Date(editingDeliveryDateValue) : null}
                onChange={(date: Date | null) => {
                  if (date) {
                    const formattedDate = Method.convertDateToFormat(date, "YYYY-MM-DD");
                    setEditingDeliveryDateValue(formattedDate);
                  }
                }}
                filterDate={(date: Date) => isDeliveryDateSelectable(date, order)}
                dateFormat="dd/MM/yyyy"
                placeholderText="Select delivery date"
                disabled={isSaving}
              />
              </div>
            ) : (
              <span className="fs-15 fw-500">
                {order.shipment.date
                  ? Method.convertDateToDDMMYYYY(order.shipment.date)
                  : "-"}
              </span>
            )}

            {canEdit && !isEditing && (
              <img
                src={editIcon}
                alt="edit"
                className="cursor-pointer"
                onClick={() => handleStartDeliveryDateEdit(order)}
                width={25}
                height={25}
              />
            )}

            {isEditing && (
              <>
                <img
                  src={greenCheck}
                  alt="save"
                  className={isSaving ? "opacity-50" : "cursor-pointer"}
                  onClick={() => !isSaving && handleSaveDeliveryDate(order)}
                  width={25}
                  height={25}
                />
                <img
                  src={CrossIcon}
                  alt="cancel"
                  className={isSaving ? "opacity-50" : "cursor-pointer"}
                  onClick={() => !isSaving && handleCancelDeliveryDateEdit()}
                  width={25}
                  height={25}
                />
              </>
            )}
          </div>
        );
      },
    },
    {
      header: "Total Amount",
      accessorKey: "purchasePrice",
      cell: ({ row }) => {
        const order = row.original as PurchaseOrder;
        return (
          <>
            TSh{" "}
            {Method.getGeneralizedAmount(
              order?.variants
                ?.map((variantVal: any) =>
                  variantVal.quantityTypes?.reduce(
                    (total: number, item: any) => {
                      const totalAmount =
                        (item.stockCount) *
                        item?.purchaseAmount;
                      return total + totalAmount;
                    },
                    0
                  )
                )
                .reduce(
                  (grandTotal: number, currVal: number) => grandTotal + currVal,
                  0
                )
            )}
          </>
        );
      },
    },
    // Pending, Approved and In Process: days open, flagged once older than Settings "Old PO after (days)"
    ...([1, 2, 5].includes(Number(tabKey))
      ? [
        {
          header: "Days Open",
          accessorKey: "daysOpen",
          cell: ({ row }: { row: any }) => {
            const order = row.original as PurchaseOrder;
            const days = daysOpenOf(order._createdAt);
            const old = oldPoDays !== null && days !== null && days > oldPoDays;
            return (
              <div className="d-flex flex-column align-items-start gap-1">
                <span className={`fs-15 ${old ? "fw-bold text-danger" : "fw-500"}`}>{getDaysOpenLabel(order._createdAt)}</span>
                {old && (
                  <span
                    className="badge badge-light-danger"
                    title={`Open for more than ${oldPoDays} days (Settings > Replenishment & Draft PO > Old PO after). Not counted in Order Needed of the Inventory Replenishment Report.${Number(tabKey) === 1 ? " Draft POs with these products need super admin approval." : ""}`}
                  >
                    Old PO: over {oldPoDays} days
                  </span>
                )}
              </div>
            );
          },
        } as ColumnDef<PurchaseOrder>,
      ]
      : []),
    ...([5, 9].includes(Number(tabKey))
      ? [
        {
          header: "GRNs",
          accessorKey: "deliveries",
          cell: ({ row }: { row: any }) => {
            const order = row.original as PurchaseOrder;
            const grnCount = Array.isArray(order.deliveries)
              ? order.deliveries.length
              : 0;

            return (
              <div className="d-flex flex-column align-items-start gap-2">
                <span className="fs-15 fw-600">{grnCount}</span>
                {grnCount > 0 ? (
                  <Button
                    variant="link"
                    className="btn-flush fs-16 fw-600 text-decoration-underline p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateToGrnSection(order);
                    }}
                  >
                    View grns
                  </Button>
                ) : (
                  <span className="text-muted">-</span>
                )}
              </div>
            );
          },
        } as ColumnDef<PurchaseOrder>,
      ]
      : []),

    // {
    //   header: "Actions",
    //   accessorKey: "actions",
    //   cell: ({ row }) => {
    //     const order = row.original as PurchaseOrder;
    //     return (
    //       <div className="d-flex gap-2">
    //         <Button className="btn-table">View details</Button>
    //       </div>
    //     );
    //   },
    // },
  ];

  const fetchPurchaseOrders = async (
    pageNo: number,
    limit: number,
    status?: any,
    search?: string,
    fromDate?: string,
    toDate?: string,
    cat: any = selectedCategory,
    subCat: any = selectedSubCategory,
    groupCat: any = selectedGroupCategory,
    bId: string = businessId
  ) => {
    // the Draft PO tab loads its own data (DraftPoList)
    if (status === DRAFT_PO_TAB) return;
    setLoading(true);
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: "_createdAt",
      sortOrder: -1,
      needCount: true,
      searchTerm: search || "",
      fromDate: fromDate
        ? Method.convertDateToFormat(fromDate, "YYYY-MM-DD")
        : "",
      toDate: toDate ? Method.convertDateToFormat(toDate, "YYYY-MM-DD") : "",
    };

    if (cat?.id) {
      params.category = cat.id;
    }

    if (subCat?.id) {
      params.subCategory = subCat.id;
    }

    if (groupCat?.id) {
      params.groupCategory = groupCat.id;
    }

      if (bId) {
        params.businessId = bId;
      }

    if (status === 9) {
      {
        params = {
          ...params,
          "status[0]": 6,
        };
      }
    } else if (status === 5) {
      params = {
        ...params,
        "status[0]": 9,
      };
    } else {
      if (status.length) {
        status.forEach((val: any, index: number) => {
          params = {
            ...params,
            [`status[${index}]`]: val,
          };
        });
      } else {
        params = {
          ...params,
          "status[0]": status,
        };
      }
    }
    let apiService = new APICallService(
      PURCHASEORDER.listPurchaseOrders,
      params,
      "",
      "",
      "",
      "",
      "",
      selectedWarehouse?._id
    );
    let response = await apiService.callAPI();
    response = response ? response : null;
    if (response?.records) {
      if (response?.total) {
        setTotalRecords(response?.total);
      }
      setGoods(response?.records);
    } else {
      setGoods([]);
    }
    setDataFetched(true);
    setLoading(false);
  };

  const fetchVendorsList = async () => {
    try {
      const params: any = {
        "status[]": 4,
      };

      const apiService = new APICallService(
        vendor.listVendor,
        params,
        "",
        "",
        "",
        "",
        AllVendors
      );

      const response = await apiService.callAPI();
      setVendorData(response?.records || []);
    } catch (err) {
      setVendorData([]);
    }
  };

  useEffect(() => {
    fetchVendorsList();
  }, []);

  // After creating a PO, clear sticky filters and reload Pending with a clean query.
  useEffect(() => {
    if (!state?.refresh) return;
    if (!selectedWarehouse?.value) return;

    setSearch("");
    setStartDate(null);
    setEndDate(null);
    setBusinessId("");
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedGroupCategory(null);
    setSubCategories([]);
    setGroupCategories([]);
    setPage(1);
    setTabKey(1);

    if (hasTabPermission(1)) {
      fetchPurchaseOrders(1, pageLimit, 1, "", undefined, undefined, null, null, null, "");
    }
  }, [state?.refresh, selectedWarehouse?.value]);

  useEffect(() => {
    if (selectedWarehouse?.value) {
      if (hasTabPermission(tabKey)) {
        fetchPurchaseOrders(
          page,
          pageLimit,
          tabKey,
          search,
          startDate?.toISOString(),
          endDate?.toISOString()
        );
      }
      fetchCategory(1, PAGE_LIMIT);
    }
  }, [selectedWarehouse?.value]);

  const fetchCategory = async (
    page: number,
    limit: number,
    search: string = ""
  ) => {
    setFetchCategoryLoader(true);
    let params: any = {
      pageNo: page,
      limit: limit,
      searchTerm: search,
      sortKey: "title",
      sortOrder: 1,
      needCount: true,
      state: 1,
      categoriesDepth: 3,
    };
    if (selectedWarehouse?.value) {
      params.warehouse = selectedWarehouse?.value;
    }
    let apiService = new APICallService(
      master.categoryList,
      params,
      "",
      "",
      "",
      "",
      "",
      selectedWarehouse?.value
    );
    let response = await apiService.callAPI();
    if (response?.records) {
      const formattedCategories = response.records.map((val: any) => ({
        value: val._id,
        name: val.title,
        id: val._id,
        image: val.image,
        label: getCategoryLabel(val),
        title:
          val.title +
          " " +
          (val.categories?.map((c: any) => c.title).join(" ") || ""),
        subCategories:
          val.categories?.map((sub: any) => ({
            value: sub._id,
            name: sub.title,
            id: sub._id,
            label: getSubCategoryLabel(sub),
            title: sub.title,
            groupCategories:
              sub.categories?.map((group: any) => ({
                value: group._id,
                name: group.title,
                id: group._id,
                label: getSubCategoryLabel(group),
                title: group.title,
              })) || [],
          })) || [],
      }));
      setTotalCategory(response.total);
      setCategories((prev: any) =>
        page === 1 ? formattedCategories : [...prev, ...formattedCategories]
      );
    } else {
      if (page === 1) setCategories([]);
    }
    setFetchCategoryLoader(false);
  };

  const handleCategoryFilter = async (value: any) => {
    setSelectedCategory(value);
    setSubCategories(value?.subCategories || []);
    setSelectedSubCategory(null);
    setGroupCategories([]);
    setSelectedGroupCategory(null);
    setPage(1);
    const sanitizedVal = sanitizeForStorage(value);
    setKey(listPurchaseOrder.category, sanitizedVal);
    setKey(listPurchaseOrder.subCategory, null);
    setKey(listPurchaseOrder.groupCategory, null);
    setKey(listPurchaseOrder.page, 1);
    await fetchPurchaseOrders(
      1,
      pageLimit,
      tabKey,
      search,
      startDate?.toISOString(),
      endDate?.toISOString(),
      value,
      null,
      null
    );
  };

  const handleSubCategoryFilter = async (value: any) => {
    setSelectedSubCategory(value);
    setGroupCategories(value?.groupCategories || []);
    setSelectedGroupCategory(null);
    setPage(1);
    const sanitizedVal = sanitizeForStorage(value);
    setKey(listPurchaseOrder.subCategory, sanitizedVal);
    setKey(listPurchaseOrder.groupCategory, null);
    setKey(listPurchaseOrder.page, 1);
    await fetchPurchaseOrders(
      1,
      pageLimit,
      tabKey,
      search,
      startDate?.toISOString(),
      endDate?.toISOString(),
      selectedCategory,
      value,
      null
    );
  };

  const handleGroupCategoryFilter = async (value: any) => {
    setSelectedGroupCategory(value);
    setPage(1);
    const sanitizedVal = sanitizeForStorage(value);
    setKey(listPurchaseOrder.groupCategory, sanitizedVal);
    setKey(listPurchaseOrder.page, 1);
    await fetchPurchaseOrders(
      1,
      pageLimit,
      tabKey,
      search,
      startDate?.toISOString(),
      endDate?.toISOString(),
      selectedCategory,
      selectedSubCategory,
      value
    );
  };

  useEffect(() => {
    if (dataFetched) {
      let initialScrollPosition: any = getKey(listPurchaseOrder.scrollPosition);
      if (initialScrollPosition) {
        ElementAnimateUtil.scrollTo(parseInt(initialScrollPosition));
        setDataFetched(false);
      }
    }
  }, [dataFetched]);

  // Clean up malformed search values on mount
  useEffect(() => {
    const savedSearch = getKey(listPurchaseOrder.search);
    if (savedSearch) {
      const searchStr =
        typeof savedSearch === "string" ? savedSearch : String(savedSearch);
      const trimmed = searchStr.trim();
      if (
        trimmed === '""' ||
        trimmed === '"""' ||
        trimmed === '"' ||
        (trimmed.match(/["\\|]/g)?.length &&
          trimmed.match(/["\\|]/g)!.length / trimmed.length > 0.3)
      ) {
        removeKey(listPurchaseOrder.search);
        setSearch("");
      }
    }
  }, []);

  const tabs = [
    ...(hasTabPermission(DRAFT_PO_TAB)
      ? [
        {
          label: "Draft PO",
          value: DRAFT_PO_TAB,
          count: draftCount,
        },
      ]
      : []),
    ...(currentUser?.userType === Admin ||
      currentUser?.roleAndPermission?.roleType === "R0001" ||
      currentUser?.roleAndPermission?.roleType === "R0003" ||
      currentUser?.roleAndPermission?.roleType === "R0012" ||
      Method.hasPermission(PermissionModule.POManagement,Permissions.Pending_Tab,currentUser)
      ? [
        {
          label: "Pending",
          value: 1,
          count:loading?NaN:totalRecords,
        },
      ]
      : []),
    ...(currentUser?.userType === Admin || Method.hasPermission(PermissionModule.POManagement,Permissions.Approved_Tab,currentUser)?[{
      label: "Approved",
      value: 2,
      count:loading?NaN:totalRecords,
    }]:[]),
    ...(currentUser?.userType === Admin || Method.hasPermission(PermissionModule.POManagement,Permissions.In_Process_Tab,currentUser)? [{
      label: "In Process",
      value: 5,
      count:loading?NaN:totalRecords,
    }]:[]),
     ...(currentUser?.userType === Admin || Method.hasPermission(PermissionModule.POManagement,Permissions.Completed_Tab,currentUser)?
    [{
      label: "Completed",
      value: 9,
      count:loading?NaN:totalRecords,
    }]:[]),
    ...(currentUser?.userType === Admin ||
      Method.hasPermission(PermissionModule.POManagement,Permissions.Rejected_Tab,currentUser) ||
      currentUser?.roleAndPermission?.roleType === "R0001" ||
      currentUser?.roleAndPermission?.roleType === "R0003" ||
      currentUser?.roleAndPermission?.roleType === "R0012"
      ? [
        {
          label: "Rejected",
          value: 4,
          count: loading?NaN:totalRecords,
        }
      ]
      : []),
 ...(currentUser?.userType === Admin ||
      Method.hasPermission(PermissionModule.POManagement,Permissions.Cancelled_Tab,currentUser)?[
           {
          label: "Cancelled",
          value: 3,
          count: loading?NaN:totalRecords,
        }]:[])
  ];

  const handleTabChange = async (value: string | number) => {
    const numericValue = typeof value === "string" ? parseInt(value) : value;
    setTabKey(numericValue);
    setPage(1);
    setKey(listPurchaseOrder.page, 1);
    setKey(listPurchaseOrder.currentTab, numericValue);
    if (hasTabPermission(numericValue)) {
      await fetchPurchaseOrders(1, pageLimit, numericValue, search, startDate?.toISOString(), endDate?.toISOString());
    }
  };

  const debounce = useDebounce(fetchPurchaseOrders, 300);
  const handleSearch = async (input: string) => {
    setSearch(input);
    setPage(1);
    setKey(listPurchaseOrder.page, 1);
    setKey(listPurchaseOrder.search, input, false);

    await debounce(1, pageLimit, tabKey, input, startDate?.toISOString(), endDate?.toISOString());
  };

 const handleDateFilter = async (event: any) => {
  const start = event?.[0];
  const end = event?.[1];

  setStartDate(start);
  setEndDate(end);
  setPage(1);
  setKey(listPurchaseOrder.page, 1);

  // When both dates selected
  if (start && end) {
    const startDateFormatted = Method.convertDateToFormat(start, "YYYY-MM-DD");
    const endDateFormatted = Method.convertDateToFormat(end, "YYYY-MM-DD");

    setKey(listPurchaseOrder.filterStartDate, start, false);
    setKey(listPurchaseOrder.filterEndDate, end, false);

    await fetchPurchaseOrders(
      1,
      pageLimit,
      tabKey,
      search,
      startDateFormatted,
      endDateFormatted
    );
  }

  // When datepicker is cleared
  if (!start && !end) {
    removeKey(listPurchaseOrder.filterStartDate);
    removeKey(listPurchaseOrder.filterEndDate);

    await fetchPurchaseOrders(
      1,
      pageLimit,
      tabKey,
      search,
      "",
      ""
    );
  }
};

  const handleCurrentPage = async (val: number) => {
    if (val === page || val.toString() === "...") return;
    setPage(val);
    setKey(listPurchaseOrder.page, val);
    await fetchPurchaseOrders(
      val,
      pageLimit,
      tabKey,
      search,
      startDate?.toISOString(),
      endDate?.toISOString()
    );
  };
  const handleNextPage = async (val: number) => {
    setPage(val + 1);
    setKey(listPurchaseOrder.page, val + 1);
    await fetchPurchaseOrders(
      val + 1,
      pageLimit,
      tabKey,
      search,
      startDate?.toISOString(),
      endDate?.toISOString()
    );
  };
  const handlePreviousPage = async (val: number) => {
    setPage(val - 1);
    setKey(listPurchaseOrder.page, val - 1);
    await fetchPurchaseOrders(
      val - 1,
      pageLimit,
      tabKey,
      search,
      startDate?.toISOString(),
      endDate?.toISOString()
    );
  };
  const handlePageLimit = async (event: any) => {
    setPage(1);
    setPageLimit(+event.target.value);
    setKey(listPurchaseOrder.page, 1);
    setKey(listPurchaseOrder.limit, parseInt(event.target.value));
    await fetchPurchaseOrders(
      1,
      event.target.value,
      tabKey,
      search,
      startDate?.toISOString(),
      endDate?.toISOString()
    );
  };

  return (
    <div className="layout-padding">
      <DynamicListLayout
        title="Purchase Orders"
        variant="withTabs"
        activeTab={tabKey}
        onTabChange={handleTabChange}
        tabs={tabs}
        action={
          <div className="d-flex gap-2 justify-content-end">
            <Button
              className="uw-btn uw-btn-primary"
              onClick={() => {
                navigate('/UProcure/purchase-order-management/add-purchase-order');
                setKey(listPurchaseOrder.search, search, false);
                setKey(listPurchaseOrder.filterStartDate, startDate, false);
                setKey(listPurchaseOrder.filterEndDate, endDate, false);
              }}
            >
              Add Purchase Order
            </Button>
            <Button
              className="uw-btn uw-btn-primary"
              onClick={() => {
                setShowImportModal(true);
              }}
            >
              Import PO from Excel
            </Button>
          </div>
        }
        secondaryAction={tabKey === DRAFT_PO_TAB ? undefined : (
          <div className='po-list-secondary-action mt-4'>
            <div className='po-filter-row po-filter-row-top'>
              <div className='po-filter-col-70'>
                <CustomSearchInput
                  backgroundColor="#ffffff"
                  placeholder="Search by Supplier, Product, PO number"
                  defaultValue={search}
                  onSearch={(value) => handleSearch(value)}
                />
              </div>
              <div className='po-filter-col-30'>
                <CustomDatePicker
                  onChange={handleDateFilter}
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  dateFormat="dd/MM/yyyy"
                  showFullMonthYearPicker={true}
                  placeholderText="Select dates"
                  isClearable={true}
                  inputTextBG="bg-white"
                />
              </div>
            </div>

            <div className='po-filter-row po-filter-row-bottom'>
              <div className='po-filter-col-25'>
                <CustomSelect
                  placeholder="Filter by Category"
                  backgroundColor="#ffffff"
                  value={selectedCategory}
                  isLoading={fetchCategoryLoader}
                  disabled={loading}
                  options={categories}
                  onChange={handleCategoryFilter}
                  onMenuScrollToBottom={() => {
                    if (categories.length < totalCategory) {
                      const nextPage = categoryPage + 1;
                      setCategoryPage(nextPage);
                      fetchCategory(nextPage, PAGE_LIMIT, categorySearch);
                    }
                  }}
                  onInputChange={(val: string, { action }: any) => {
                    if (action === "input-change") {
                      setCategorySearch(val);
                      setCategoryPage(1);
                      fetchCategory(1, PAGE_LIMIT, val);
                    }
                  }}
                  onMenuOpen={() => {
                    setCategoryPage(1);
                    fetchCategory(1, PAGE_LIMIT, categorySearch);
                  }}
                  onMenuClose={() => {
                    setCategorySearch("");
                  }}
                  isClearable={true}
                  menuPortalZIndex={99}
                />
              </div>
              <div className='po-filter-col-25'>
                <CustomSelect
                  placeholder="Filter by Sub Category"
                  backgroundColor="#ffffff"
                  value={selectedSubCategory}
                  options={subCategories}
                  onChange={handleSubCategoryFilter}
                  isClearable={true}
                  disabled={!selectedCategory || loading}
                  menuPortalZIndex={99}
                />
              </div>
              <div className='po-filter-col-25'>
                <CustomSelect
                  placeholder="Filter by Group Category"
                  backgroundColor="#ffffff"
                  value={selectedGroupCategory}
                  options={groupCategories}
                  onChange={handleGroupCategoryFilter}
                  isClearable={true}
                  disabled={!selectedSubCategory || loading}
                  menuPortalZIndex={99}
                />
              </div>
              <div className='po-filter-col-25'>
                <CustomSelect
                  backgroundColor="#ffffff"
                  menuPortalZIndex={99}
                  isLoading={loading}
                  placeholder='Select Supplier'
                  options={
                    vendorData && vendorData.length
                      ? vendorData.map((val: any) => ({
                        label: (
                          <>
                            <div className="symbol symbol-30px border me-3">
                              <img
                                src={val.image || b1}
                                className="object-fit-contain"
                                alt=""
                              />
                            </div>
                            <span className="fs-16 fw-600 text-black mb-0">
                              {val.ownerName}
                            </span>
                          </>
                        ),
                        value: val._id,
                        creationDate: val._createdAt,
                        title: val.ownerName,
                      }))
                      : []
                  }
                  value={
                    businessId
                      ? vendorData
                        .filter((v: any) => v._id === businessId)
                        .map((val: any) => ({
                          label: (
                            <>
                              <div className="symbol symbol-30px border me-3">
                                <img
                                  src={val.image || b1}
                                  className="object-fit-contain"
                                  alt=""
                                />
                              </div>
                              <span className="fs-16 fw-600 text-black mb-0">
                                {val.ownerName}
                              </span>
                            </>
                          ),
                          value: val._id,
                          title: val.ownerName,
                        }))[0] || null
                      : null
                  }
                  isDisabled={loading}
                  onMenuScrollToBottom={onMenuScrollToBottom}
                  onMenuClose={onMenuClose}
                  onMenuOpen={onMenuOpen}
                  onInputChange={(newValue: any, { action }: any) => {
                    if (action === "input-change") {
                      handleInputChange();
                    }
                  }}
                  onChange={handleVendor}
                  isClearable={true}
                />
              </div>
            </div>
          </div>
        )}
        totalRecords={tabKey === DRAFT_PO_TAB ? draftTotal : totalRecords}
        page={page}
        pageLimit={pageLimit}
        handleCurrentPage={handleCurrentPage}
        handleNextPage={handleNextPage}
        handlePreviousPage={handlePreviousPage}
        handlePageLimit={handlePageLimit}
      >
        {tabKey === DRAFT_PO_TAB && hasTabPermission(DRAFT_PO_TAB) ? (
          <DraftPoList
            warehouse={selectedWarehouse?.value}
            onCount={setDraftCount}
            page={page}
            pageLimit={pageLimit}
            onTotal={(n) => {
              setDraftTotal(n);
              // back to page 1 when the search / view leaves fewer pages
              if ((page - 1) * pageLimit >= n && page !== 1) setPage(1);
            }}
          />
        ) : hasTabPermission(tabKey) ? (
          <CustomTable data={goods} columns={columns} loading={loading} onRowClick={(row) => navigateToOrderDetails(row as PurchaseOrder)}/>
        ) : (
          <div className="text-center py-10">
            <span className="text-muted fs-15 fw-500">
              You don't have permission to view this tab.
            </span>
          </div>
        )}
      </DynamicListLayout>

      <ViewGoodsRequestProducts
        show={showRequestedGoods}
        onHide={() => setRequestedGoods(false)}
        products={selectedOrderProducts}
      />

      <ImportPOModal
        show={showImportModal}
        onHide={() => setShowImportModal(false)}
        onSuccess={() => {
          handleTabChange(1); // View Pending status
        }}
      />
    </div>
  );
};
export default PurchaseOrderList;
